import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, message, Tabs } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../utils/axiosInstance";

export default function SelectTemplateModal({ contract, onSelectTemplate, editable_user }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();
  const [editorContent, setEditorContent] = useState("");

  // ✅ Default Templates (not from API)
  const defaultTemplates = [
    {
      id: "d1",
      name: "New Business Partner Agreement",
      subject: "New Business Partner Agreement",
      isDefault: true,
      created: false,
      body: `
        <p>Hi Team,</p>
        <p>We appreciate your collaboration and would like to move forward with the formalization of our business relationship through the agreement outlined below. The Agreement has been shared for your inputs and approval.</p>
        <p><strong>Details:</strong></p>
        <ul>
          <li><strong>Name of Business Partner:</strong> ${contract?.party_name || ""}</li>
          <li><strong>Name of Business Entity:</strong> ${contract?.party || ""}</li>
          <li><strong>Type of Agreement:</strong> ${contract?.agreement_type || ""}</li>
          <li><strong>Document Id:</strong> ${contract?.document_id || ""}</li>
          <li><strong>Status</strong> ${contract?.status || ""}</li>
          <li><strong>Sender Email Name & ID:</strong> ${editable_user}</li>
          <li><strong>Remarks:</strong></li>
          <li><strong>Additional attachments:</strong></li>
        </ul>
        <p>Regards,<br/>Contracts Team</p>
      `,
    },
    {
      id: "d2",
      name: "Contract Renewal Request",
      subject: "Contract Renewal Request",
      isDefault: true,
      created: false,
      body: `
        <p>Dear Team,</p>
        <p>This is a reminder regarding the renewal of your existing agreement with us. Kindly review the draft renewal agreement and provide your feedback at the earliest.</p>
        <p><strong>Details:</strong></p>
        <ul>
          <li><strong>Contract Name:</strong> ${contract?.agreement_type || ""}</li>
          <li><strong>Status:</strong>  ${contract?.status || ""}</li>
          <li><strong>Document Id:</strong>  ${contract?.document_id || ""}</li>
        </ul>
        <p>We look forward to your confirmation.</p>
        <p>Best Regards,<br/>Legal Team</p>
      `,
    },
  ];

  // ✅ Fetch Templates from API
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/contract/email-templates/");
      const apiTemplates = res.data.map((t) => ({ ...t, isDefault: false }));

      // Merge default + API
      setTemplates([...apiTemplates, ...defaultTemplates]);
    } catch (err) {
      message.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Save Template
  const handleSave = async (values) => {
    try {
      const payload = {
        ...values,
        body: editorContent,
        is_active: true,
        user: 3,
      };

      if (editingTemplate && !editingTemplate.isDefault) {
        // Update existing API template
        await axiosInstance.put(`/contract/email-templates/${editingTemplate.id}/`, {
          ...editingTemplate,
          ...payload,
        });
        message.success("Template updated successfully");
      } else {
        // First-time create (either new OR default template editing)
        await axiosInstance.post("/contract/email-templates/", payload);
        message.success("Template created successfully");
      }

      fetchTemplates();
      setEditingTemplate(null);
      form.resetFields();
      setEditorContent("");
    } catch (err) {
      message.error("Error saving template");
    }
  };

  const handleSelect = (template) => {
    onSelectTemplate({
      subject: template.subject,
      body: template.body,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (open) fetchTemplates();
  }, [open]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  return (
    <div className="z-[500000000]">
      <Button size="small" type="link" className="text-xs" onClick={() => setOpen(true)}>
        Select Template
      </Button>

      <Modal
        title="Manage Email Templates"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={1500}
        zIndex={5000000}
      >
        <Tabs size="small" defaultActiveKey="1">
          {/* All Templates Tab */}
          <Tabs.TabPane tab="All Templates" key="1">
            <div className="grid grid-cols-5 gap-2">
              {templates.map((tpl) => (
                <div key={tpl.id} className="border p-1 rounded-lg hover:shadow-lg relative flex flex-col justify-between">
                  <div>
                    <h1 className="font-bold underline pb-1 text-[12px]">{tpl.name}</h1>
                    <h1 className="font-bold pb-1 text-[10px]">Subject : {tpl.subject}</h1>
                    <div className="text-[10px]" dangerouslySetInnerHTML={{ __html: tpl.body }} />
                  </div>

                  <div className="flex justify-between mt-2 float-end">
                    <Button size="small" onClick={() => handleSelect(tpl)}>
                      Use
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingTemplate(tpl);
                        form.setFieldsValue(tpl.isDefault ? { name: tpl.name, subject: "" } : tpl);
                        setEditorContent(tpl.body);
                      }}
                    >
                      {tpl.isDefault ? "Create" : "Edit"}
                    </Button>
                  </div>

                  {/* Inline Editor */}
                  {editingTemplate?.id === tpl.id && (
                    <div className="mt-4 border-t pt-4">
                      <Form form={form} layout="vertical" onFinish={handleSave}>
                        <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item label="Body (HTML)">
                          <ReactQuill
                            theme="snow"
                            value={editorContent}
                            onChange={setEditorContent}
                            modules={quillModules}
                            style={{ height: "200px", marginBottom: "50px" }}
                          />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                          {tpl.isDefault ? "Create Template" : "Update Template"}
                        </Button>
                        <Button
                          className="ml-2"
                          onClick={() => {
                            setEditingTemplate(null);
                            setEditorContent("");
                          }}
                        >
                          Cancel
                        </Button>
                      </Form>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Tabs.TabPane>

          {/* Create Template Tab */}
          <Tabs.TabPane tab="Create Template" key="2">
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Body (HTML)">
                <ReactQuill
                  theme="snow"
                  value={editorContent}
                  onChange={setEditorContent}
                  modules={quillModules}
                  style={{ height: "250px", marginBottom: "50px" }}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Template
              </Button>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </div>
  );
}
